import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import {
  BlockStack,
  FormLayout,
  InlineStack,
  TextField,
  Form,
  Button,
  Banner,
  Page,
  Select,
  LegacyStack,
  Text,
  Checkbox,
  RadioButton,
} from "@shopify/polaris";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { submit } from "./customer-form.slice";
import { fetchCustomer } from "containers/customer/customer-container.slice";
import { fetchById } from "services/customers.service";
import UserSelect from "features/user-select/user-select";
import Loading from "components/loading/Loading";
import "./style.scss";
import { validateEmail } from "common/validator";
import { getAllCountryOptions, getAllCountryISOptions } from "constants/countries";
import { splitVatCode, validateVatCode } from "utils/helperFunctions";
import CustomerRegistrySearch from "features/customer-registry-search/customer-registry-search";
import { fillIfEmpty } from "./fill-if-empty";

const EMPTY_BANK_ACCOUNT = {
  iban: "",
  accountNumber: "",
  bankName: "",
  swiftCode: "",
  currency: "",
  comment: "",
  isPrimary: false,
};

/** One click instead of typing; the field itself stays free text. */
const POPULAR_CURRENCIES = ["EUR", "USD", "GBP", "PLN"];

function applyCustomerEntityToForm(customer, roleUser, setters) {
  const {
    setCustomerId,
    setEmail,
    setName,
    setLegalName,
    setPhone,
    setContactPerson,
    setCountry,
    setVatCodeLetters,
    setVatCodeFigures,
    setCompanyCode,
    setStreet,
    setHouseNumber,
    setCity,
    setRegion,
    setZipCodeLetters,
    setZipCodeFigures,
    setAddress,
    setMaxAllowedDebt,
    setBankAccounts,
    setPaymentDelay,
    setPrepaymentPercentage,
    setFine,
    setExternalCode,
    setAssignedUser,
    setInvoiceEmail,
    setSecondaryEmail,
    setIsVatPayer,
  } = setters;

  setCustomerId(customer.id);
  setEmail(customer.email);
  setName(customer.name);
  setLegalName(customer.legalName || "");
  setPhone(customer.phone);
  setContactPerson(customer.contactPerson);

  const customerCountry = customer.country || "LT";
  setCountry(customerCountry);

  const vatCodeSplit = customer.vatCode
    ? splitVatCode(customer.vatCode)
    : { letters: "", figures: "" };

  const vatLetters =
    vatCodeSplit.letters ||
    (customerCountry ? customerCountry.toUpperCase() : "");
  setVatCodeLetters(vatLetters);
  setVatCodeFigures(vatCodeSplit.figures);

  setCompanyCode(customer.companyCode);
  setStreet(customer.street || "");
  setHouseNumber(customer.houseNumber || "");
  setCity(customer.city || "");
  setRegion(customer.region || "");
  setZipCodeLetters(customer.zipCodeLetters || "");
  setZipCodeFigures(customer.zipCodeFigures || "");
  setAddress(customer.address || "");
  setMaxAllowedDebt(
    customer.maxAllowedDebt !== undefined && customer.maxAllowedDebt !== null
      ? String(customer.maxAllowedDebt)
      : ""
  );
  // Several accounts per customer (EUR + USD with the same partner). A customer
  // saved before the list exists still has only the legacy columns.
  setBankAccounts(
    customer.bankAccounts?.length > 0
      ? [...customer.bankAccounts]
          .sort((a, b) => {
            if (!!a.isPrimary !== !!b.isPrimary) {
              return a.isPrimary ? -1 : 1;
            }
            return (a.sortOrder || 0) - (b.sortOrder || 0);
          })
          .map((account) => ({
            iban: account.iban || "",
            accountNumber: account.accountNumber || "",
            bankName: account.bankName || "",
            swiftCode: account.swiftCode || "",
            currency: account.currency || "",
            comment: account.comment || "",
            isPrimary: !!account.isPrimary,
          }))
      : [
          {
            ...EMPTY_BANK_ACCOUNT,
            iban: customer.bankIban || "",
            accountNumber: customer.bankIban2 || "",
            bankName: customer.bankName || "",
            swiftCode: customer.bankSwiftCode || "",
            isPrimary: true,
          },
        ]
  );
  setPaymentDelay(
    customer.paymentDelay !== undefined && customer.paymentDelay !== null
      ? String(customer.paymentDelay)
      : ""
  );
  setPrepaymentPercentage(
    customer.prepaymentPercentage !== undefined &&
      customer.prepaymentPercentage !== null
      ? String(customer.prepaymentPercentage)
      : ""
  );
  setFine(
    customer.fine !== undefined && customer.fine !== null
      ? String(customer.fine)
      : ""
  );
  setExternalCode(customer.externalCode || "");
  setAssignedUser(customer.assignedUser?.id || roleUser);
  setInvoiceEmail(customer.invoiceEmail || "");
  setSecondaryEmail(customer.secondaryEmail || "");
  setIsVatPayer(customer.isVatPayer);
}

function CustomerForm({ onSubmit, id: editId }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams();
  const { isLoading, errMsg, successMessage } = useSelector(
    (state) => state.customerFormReducer
  );
  const { role } = useSelector((state) => state.authReducer);

  /** Modal passes `id` like other forms; never infer from URL (avoids wrong data under /customers/:id/...). */
  const editTargetId = editId || "";

  const [customerId, setCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [phone, setPhone] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [vatCodeLetters, setVatCodeLetters] = useState("");
  const [vatCodeFigures, setVatCodeFigures] = useState("");
  const [companyCode, setCompanyCode] = useState("");
  const [street, setStreet] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [city, setCity] = useState("");
  const [region, setRegion] = useState("");
  const [zipCodeLetters, setZipCodeLetters] = useState("");
  const [zipCodeFigures, setZipCodeFigures] = useState("");
  const [country, setCountry] = useState("LT");
  const [address, setAddress] = useState("");
  const [maxAllowedDebt, setMaxAllowedDebt] = useState("");
  const [bankAccounts, setBankAccounts] = useState([
    { ...EMPTY_BANK_ACCOUNT, isPrimary: true },
  ]);
  const [paymentDelay, setPaymentDelay] = useState("");
  const [prepaymentPercentage, setPrepaymentPercentage] = useState("");
  const [fine, setFine] = useState("");
  const [externalCode, setExternalCode] = useState("");
  const [assignedUser, setAssignedUser] = useState(role?.user || "");
  const [invoiceEmail, setInvoiceEmail] = useState("");
  const [secondaryEmail, setSecondaryEmail] = useState("");
  const [isVatPayer, setIsVatPayer] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const [entityLoading, setEntityLoading] = useState(false);
  const [entityLoadError, setEntityLoadError] = useState(null);

  const resetFormForCreate = useCallback(() => {
    setCustomerId("");
    setEmail("");
    setName("");
    setLegalName("");
    setPhone("");
    setContactPerson("");
    setCountry("LT");
    setVatCodeLetters("LT");
    setVatCodeFigures("");
    setCompanyCode("");
    setStreet("");
    setHouseNumber("");
    setCity("");
    setRegion("");
    setZipCodeLetters("");
    setZipCodeFigures("");
    setAddress("");
    setMaxAllowedDebt("");
    setBankAccounts([{ ...EMPTY_BANK_ACCOUNT, isPrimary: true }]);
    setPaymentDelay("");
    setPrepaymentPercentage("");
    setFine("");
    setExternalCode("");
    setAssignedUser(role?.user || "");
    setInvoiceEmail("");
    setSecondaryEmail("");
    setIsVatPayer(null);
    setFormErrors({});
  }, [role?.user]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!companyId) return;

      if (!editTargetId) {
        resetFormForCreate();
        setEntityLoadError(null);
        return;
      }

      setEntityLoading(true);
      setEntityLoadError(null);
      try {
        const entity = await fetchById(companyId, editTargetId);
        if (cancelled) return;
        applyCustomerEntityToForm(entity, role?.user, {
          setCustomerId,
          setEmail,
          setName,
          setLegalName,
          setPhone,
          setContactPerson,
          setCountry,
          setVatCodeLetters,
          setVatCodeFigures,
          setCompanyCode,
          setStreet,
          setHouseNumber,
          setCity,
          setRegion,
          setZipCodeLetters,
          setZipCodeFigures,
          setAddress,
          setMaxAllowedDebt,
          setBankAccounts,
          setPaymentDelay,
          setPrepaymentPercentage,
          setFine,
          setExternalCode,
          setAssignedUser,
          setInvoiceEmail,
          setSecondaryEmail,
          setIsVatPayer,
        });
      } catch (e) {
        if (!cancelled) {
          setEntityLoadError(e);
        }
      } finally {
        if (!cancelled) {
          setEntityLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, editId, role?.user, resetFormForCreate]);

  const validateForm = () => {
    const errors = {};

    if (!name) errors.name = t("field_is_required");
    if (!validateEmail(email)) errors.email = t("invalid_email");
    if (!companyCode) errors.companyCode = t("field_is_required");
    if (!assignedUser) errors.assignedUser = t("field_is_required");
    if (invoiceEmail && !validateEmail(invoiceEmail))
      errors.invoiceEmail = t("invalid_email");
    if (secondaryEmail && !validateEmail(secondaryEmail))
      errors.secondaryEmail = t("invalid_email");

    if (isVatPayer === null || isVatPayer === undefined) {
      errors.isVatPayer = t("customer_form-isVatPayer_required");
    }

    if (isVatPayer) {
      const vatCodeErrors = validateVatCode(
        vatCodeLetters,
        vatCodeFigures,
        t,
        true
      );
      if (vatCodeErrors) {
        if (vatCodeErrors.vatCodeLetters) {
          errors.vatCodeLetters = vatCodeErrors.vatCodeLetters;
        }
        if (vatCodeErrors.vatCodeFigures) {
          errors.vatCodeFigures = vatCodeErrors.vatCodeFigures;
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBankAccountChange = useCallback(
    (index, field) => (rawValue) => {
      let value = rawValue;
      if (field === "iban" || field === "accountNumber" || field === "swiftCode") {
        value = (value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
      }
      if (field === "currency") {
        value = (value || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3);
      }
      setBankAccounts((prev) =>
        prev.map((account, i) =>
          i === index ? { ...account, [field]: value } : account
        )
      );
    },
    []
  );

  const handleAddBankAccount = useCallback(() => {
    setBankAccounts((prev) => [...prev, { ...EMPTY_BANK_ACCOUNT }]);
  }, []);

  /** Dropping the primary account has to leave another one primary. */
  const handleRemoveBankAccount = useCallback((index) => {
    setBankAccounts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (!next.length) {
        return [{ ...EMPTY_BANK_ACCOUNT, isPrimary: true }];
      }
      if (!next.some((account) => account.isPrimary)) {
        next[0] = { ...next[0], isPrimary: true };
      }
      return next;
    });
  }, []);

  const handleSetPrimaryBankAccount = useCallback((index) => {
    setBankAccounts((prev) =>
      prev.map((account, i) => ({ ...account, isPrimary: i === index }))
    );
  }, []);

  const handleFormSubmit = async () => {
    const isValid = validateForm();
    if (!isValid) return;

    // Rows the user opened and left blank are not accounts.
    const filledBankAccounts = bankAccounts.filter(
      (account) =>
        account.iban ||
        account.accountNumber ||
        account.bankName ||
        account.swiftCode ||
        account.comment
    );
    // The row that starts the form is blank AND primary, so filling a second
    // one and saving would drop the flag with the blank row and leave the list
    // saying nothing about which account is the primary one. Say it explicitly.
    const hasPrimary = filledBankAccounts.some((account) => account.isPrimary);
    const cleanedBankAccounts = filledBankAccounts.map((account, index) => ({
      ...account,
      isPrimary: hasPrimary ? !!account.isPrimary : index === 0,
    }));
    const primaryBankAccount =
      cleanedBankAccounts.find((account) => account.isPrimary) ||
      EMPTY_BANK_ACCOUNT;

    const formData = {
      customerId,
      email,
      name,
      legalName: legalName || null,
      phone,
      contactPerson,
      vatCode:
        isVatPayer && vatCodeLetters && vatCodeFigures
          ? `${vatCodeLetters}${vatCodeFigures}`
          : isVatPayer ? (vatCodeLetters || vatCodeFigures || "") : "",
      companyCode,
      houseNumber: houseNumber || null,
      street: street || null,
      city: city || null,
      region: region || null,
      zipCodeLetters: zipCodeLetters || null,
      zipCodeFigures: zipCodeFigures || null,
      country: country || null,
      address,
      companyId,
      maxAllowedDebt,
      bankAccounts: cleanedBankAccounts,
      // The primary account, for anything still reading the flat columns.
      bankIban: primaryBankAccount.iban,
      bankName: primaryBankAccount.bankName,
      paymentDelay,
      prepaymentPercentage,
      fine,
      externalCode,
      assignedUser,
      invoiceEmail,
      secondaryEmail,
      isVatPayer,
    };

    const id = await dispatch(submit(formData));
    if (id) {
      if (onSubmit) {
        onSubmit(id);
      } else if (companyId) {
        dispatch(fetchCustomer({ companyId, customerId: id }));
        navigate(`/app/${companyId}/customers/${id}`);
      }
    }
  };

  /**
   * Fill the form from a row the manager picked in the public register.
   *
   * Only fields the register is authoritative for. Commercial terms — credit
   * limit, payment delay, prepayment, penalty — and bank details are never
   * touched: no register knows them, and they are what the manager negotiated.
   *
   * A fresh pick replaces every register-owned field, empty ones included.
   * The later address-only call just fills the address fields that are still
   * empty with a real value, so it cannot blank what the first call filled or
   * overwrite what the manager typed in the meantime.
   */
  const applyRegistryMatch = useCallback((match, opts = {}) => {
    const write = (value, setter) => setter(value == null ? "" : String(value));

    // The address arrives in a second call, seconds after the pick. It touches
    // ONLY the address, and only fields still empty: the identifiers from the
    // first call must survive, and so must anything typed meanwhile (hence
    // the functional setters: this closure's state is long stale by then).
    if (opts.addressOnly) {
      setStreet((current) => fillIfEmpty(current, match.street));
      setHouseNumber((current) => fillIfEmpty(current, match.houseNumber));
      setCity((current) => fillIfEmpty(current, match.city));
      setRegion((current) => fillIfEmpty(current, match.region));
      setZipCodeLetters((current) => fillIfEmpty(current, match.zipCodeLetters));
      setZipCodeFigures((current) => fillIfEmpty(current, match.zipCodeFigures));
      setAddress((current) => fillIfEmpty(current, match.address));
      return;
    }

    // A fresh pick REPLACES every field the register owns, empty ones
    // included. Skipping empties would leave the previous company's address
    // and VAT code sitting under the new company's name — a counterparty
    // stitched together from two different firms, which is exactly the kind of
    // wrong that ends up on an invoice.
    // Two columns, deliberately: legalName is the FORM (UAB, SIA, GmbH) and
    // name is the company itself (Hilla). Production stores them apart, so
    // putting the whole registered string in both would be wrong on every
    // document that prints a counterparty.
    write(match.name, setName);
    write(match.legalName, setLegalName);
    write(match.companyCode, setCompanyCode);
    write(match.vatCodeLetters, setVatCodeLetters);
    write(match.vatCodeFigures, setVatCodeFigures);
    write(match.street, setStreet);
    write(match.houseNumber, setHouseNumber);
    write(match.city, setCity);
    write(match.region, setRegion);
    // The search rows carry no postal code, so this clears the previous
    // company's; the late address answer fills it in.
    write(match.zipCodeLetters, setZipCodeLetters);
    write(match.zipCodeFigures, setZipCodeFigures);
    write(match.address, setAddress);

    // The register states this outright, and it decides whether an invoice
    // carries VAT — so it is set even when false.
    setIsVatPayer(Boolean(match.isVatPayer));
    // A foreign counterparty carries its country in the VAT prefix.
    setCountry(match.country || match.vatCodeLetters || "LT");

    // Bank details and the commercial terms are deliberately untouched: no
    // register knows them, and they are what the manager negotiated.
    setFormErrors({});
  }, []);

  const handleFieldChange = (field, value) => {
    const setters = {
      name: setName,
      email: setEmail,
      companyCode: setCompanyCode,
      assignedUser: setAssignedUser,
      invoiceEmail: setInvoiceEmail,
      secondaryEmail: setSecondaryEmail,
    };

    if (setters[field]) setters[field](value);

    setFormErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  if (entityLoading) {
    return <Loading />;
  }

  return (
    <Page fullWidth title={t("customer")}>
      <div className="customer-form">
        <Form onSubmit={handleFormSubmit} className="customer-form_form">
          <FormLayout>
            <div className="form-warnings">
              {entityLoadError && (
                <Banner status="critical">{t("error")}</Banner>
              )}
              {errMsg && (
                <div className="">
                  <Banner status="critical">{t(errMsg)}</Banner>
                </div>
              )}
              {successMessage && (
                <div className="">
                  <Banner status="info">{t(successMessage)}</Banner>
                </div>
              )}
            </div>
            {!customerId && (
              <CustomerRegistrySearch
                companyId={companyId}
                onSelect={applyRegistryMatch}
              />
            )}
            <TextField
              label={t("Email")}
              id="email"
              name="email"
              value={email}
              onChange={(val) => handleFieldChange("email", val)}
              error={formErrors.email}
              requiredIndicator={true}
            />
            <TextField
              label={t("title")}
              id="name"
              name="name"
              value={name}
              onChange={(val) => handleFieldChange("name", val)}
              error={formErrors.name}
              requiredIndicator={true}
            />
            <TextField
              label={t("legal_form")}
              id="legalName"
              name="legalName"
              value={legalName}
              onChange={setLegalName}
            />
            <TextField
              label={t("invoice_email")}
              id="invoice_email"
              name="invoice_email"
              value={invoiceEmail}
              onChange={(val) => handleFieldChange("invoiceEmail", val)}
              error={formErrors.invoiceEmail}
            />
            <TextField
              label={t("secondary_email")}
              id="secondary_email"
              name="secondary_email"
              value={secondaryEmail}
              onChange={(val) => handleFieldChange("secondaryEmail", val)}
              error={formErrors.secondaryEmail}
            />
            <UserSelect
              onChange={setAssignedUser}
              value={assignedUser}
              label={"select_assigned_user"}
              isRequired={true}
            />
            <TextField
              label={t("phone")}
              id="phone"
              name="phone"
              value={phone}
              onChange={setPhone}
            />
            <TextField
              label={t("contact_person")}
              id="contactPerson"
              name="contactPerson"
              value={contactPerson}
              onChange={setContactPerson}
            />
            <TextField
              label={t("company_code")}
              id="companyCode"
              name="companyCode"
              value={companyCode}
              onChange={(val) => handleFieldChange("companyCode", val)}
              error={formErrors.companyCode}
              requiredIndicator={true}
            />
            <LegacyStack vertical gap="extraTight">
              <Text variant="bodyMd" as="p">
                {t("Ar esate PVM mokėtojas? / Are you a VAT payer?")}
              </Text>
              <LegacyStack gap="loose">
                <Checkbox
                  label={t("Taip / Yes")}
                  checked={isVatPayer === true}
                  onChange={(value) => {
                    const newValue = value ? true : null;
                    setIsVatPayer(newValue);
                    if (newValue !== null && formErrors.isVatPayer) {
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.isVatPayer;
                        return next;
                      });
                    }
                  }}
                />
                <Checkbox
                  label={t("Ne / No")}
                  checked={isVatPayer === false}
                  onChange={(value) => {
                    const newValue = value ? false : null;
                    setIsVatPayer(newValue);
                    if (newValue === false) {
                      setVatCodeLetters("");
                      setVatCodeFigures("");
                    }
                    if (newValue !== null && formErrors.isVatPayer) {
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.isVatPayer;
                        return next;
                      });
                    }
                  }}
                />
              </LegacyStack>
              {formErrors.isVatPayer && (
                <div style={{ color: "var(--p-color-text-critical)", fontSize: "var(--p-font-size-75)" }}>
                  {formErrors.isVatPayer}
                </div>
              )}
            </LegacyStack>

            {isVatPayer && (
              <FormLayout.Group>
                <Select
                  label={t("PVM Šalies / VAT Country")}
                  id="vatCodeLetters"
                  name="vatCodeLetters"
                  options={getAllCountryISOptions(t)}
                  value={vatCodeLetters}
                  onChange={(value) => {
                    setVatCodeLetters(value || "");
                    if (formErrors.vatCodeLetters) {
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.vatCodeLetters;
                        return next;
                      });
                    }
                  }}
                  error={formErrors.vatCodeLetters}
                  helpText={t("Šalies / Country")}
                />
                <TextField
                  label={t("customer_form-vatCode")}
                  id="vatCodeFigures"
                  name="vatCodeFigures"
                  value={vatCodeFigures}
                  onChange={(val) => {
                    const cleaned = val.toUpperCase().replace(/[^0-9A-Z]/g, "");
                    setVatCodeFigures(cleaned);
                    if (formErrors.vatCodeFigures) {
                      setFormErrors((prev) => {
                        const next = { ...prev };
                        delete next.vatCodeFigures;
                        return next;
                      });
                    }
                  }}
                  error={formErrors.vatCodeFigures}
                  helpText={t("(Kodas / Code)")}
                />
              </FormLayout.Group>
            )}

            <TextField
              label={t("street")}
              id="street"
              name="street"
              value={street}
              onChange={setStreet}
            />
            <TextField
              label={t("house_number")}
              id="houseNumber"
              name="houseNumber"
              value={houseNumber}
              onChange={setHouseNumber}
            />
            <TextField
              label={t("city")}
              id="city"
              name="city"
              value={city}
              onChange={setCity}
            />
            <TextField
              label={t("region")}
              id="region"
              name="region"
              value={region}
              onChange={setRegion}
            />
            <FormLayout.Group>
              <TextField
                label={t("postal_code_letters")}
                id="zipCodeLetters"
                name="zipCodeLetters"
                value={zipCodeLetters}
                onChange={setZipCodeLetters}
              />
              <TextField
                label={t("postal_code_figures")}
                id="zipCodeFigures"
                name="zipCodeFigures"
                value={zipCodeFigures}
                onChange={setZipCodeFigures}
              />
            </FormLayout.Group>
            <Select
              label={t("country_of_registration")}
              id="country"
              name="country"
              options={getAllCountryOptions(t)}
              onChange={(value) => {
                setCountry(value);
                if (!vatCodeLetters && value) {
                  setVatCodeLetters(value.toUpperCase());
                }
              }}
              value={country}
            />
            <Text as="h3" variant="headingSm">
              {t("bank_details")}
            </Text>
            {bankAccounts.map((account, index) => (
              <div
                key={`bank-account-${index}`}
                className="customer-form__bank-account"
              >
                {bankAccounts.length > 1 && (
                  <Text as="h4" variant="headingXs">
                    {t("bank_account_heading", { number: index + 1 })}
                  </Text>
                )}
                <FormLayout>
                  <FormLayout.Group>
                    <TextField
                      label={t("bank_iban")}
                      id={`bankIban-${index}`}
                      name={`bankIban-${index}`}
                      value={account.iban}
                      onChange={handleBankAccountChange(index, "iban")}
                    />
                    <TextField
                      label={t("bank_name")}
                      id={`bankName-${index}`}
                      name={`bankName-${index}`}
                      value={account.bankName}
                      onChange={handleBankAccountChange(index, "bankName")}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      label={t("bank_swift")}
                      id={`bankSwift-${index}`}
                      name={`bankSwift-${index}`}
                      value={account.swiftCode}
                      onChange={handleBankAccountChange(index, "swiftCode")}
                    />
                    <BlockStack gap="150">
                      <TextField
                        label={t("bank_account_currency")}
                        id={`bankCurrency-${index}`}
                        name={`bankCurrency-${index}`}
                        value={account.currency}
                        onChange={handleBankAccountChange(index, "currency")}
                        maxLength={3}
                        helpText={t("bank_account_currency_help")}
                      />
                      <InlineStack gap="200">
                        {POPULAR_CURRENCIES.map((code) => (
                          <Button
                            key={code}
                            size="micro"
                            variant={
                              account.currency === code
                                ? "primary"
                                : "secondary"
                            }
                            onClick={() =>
                              handleBankAccountChange(index, "currency")(code)
                            }
                          >
                            {code}
                          </Button>
                        ))}
                      </InlineStack>
                    </BlockStack>
                  </FormLayout.Group>
                  <TextField
                    label={t("bank_account_comment")}
                    id={`bankComment-${index}`}
                    name={`bankComment-${index}`}
                    value={account.comment}
                    onChange={handleBankAccountChange(index, "comment")}
                    maxLength={255}
                  />
                  <LegacyStack alignment="center">
                    <RadioButton
                      label={t("bank_account_primary")}
                      id={`bankPrimary-${index}`}
                      name="bankPrimary"
                      checked={!!account.isPrimary}
                      onChange={() => handleSetPrimaryBankAccount(index)}
                    />
                    {bankAccounts.length > 1 && (
                      <Button
                        variant="plain"
                        tone="critical"
                        onClick={() => handleRemoveBankAccount(index)}
                      >
                        {t("bank_account_remove")}
                      </Button>
                    )}
                  </LegacyStack>
                </FormLayout>
              </div>
            ))}
            <LegacyStack>
              <Button onClick={handleAddBankAccount}>
                {t("bank_account_add")}
              </Button>
            </LegacyStack>
            <TextField
              label={t("external_code")}
              id="externalCode"
              name="externalCode"
              value={externalCode}
              onChange={setExternalCode}
            />
            <Button
              primary
              onClick={handleFormSubmit}
              disabled={isLoading}
              loading={isLoading}
            >
              {t("save")}
            </Button>
          </FormLayout>
        </Form>
      </div>
    </Page>
  );
}

export default CustomerForm;

CustomerForm.propTypes = {
  onSubmit: PropTypes.func,
  id: PropTypes.string,
};

CustomerForm.defaultProps = {
  onSubmit: null,
  id: null,
};
